// Prepares a picture for a slot on the store before it is uploaded: crops it from the centre to the slot's shape
// (so it fills the placeholder without stretching or empty bars) and scales it down to a sensible size, which also
// keeps files small. Logos keep their own shape and transparency; GIFs are left as they are (animations).
export const IMAGE_SLOTS = {
  banner: { width: 1600, height: 1000, label: "16:10 banner", hint: "landscape 16:10, keep the main subject in the centre" }, // the home page banner photo
  product: { width: 1000, height: 1000, label: "square", hint: "square 1:1, product in the middle on a plain background" }, // product cards and the product page
};

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("This image could not be read. Try a JPG or PNG.")); };
    img.src = url;
  });

// Returns { file, adjusted } — adjusted is false when the picture was uploaded as it is
export async function fitImage(file, kind) {
  const slot = IMAGE_SLOTS[kind];
  if (!slot || file.type === "image/gif") return { file, adjusted: false };

  const img = await loadImage(file);
  const target = slot.width / slot.height;
  const source = img.naturalWidth / img.naturalHeight;
  // Centre crop to the slot's shape
  let sw = img.naturalWidth;
  let sh = img.naturalHeight;
  if (source > target) sw = Math.round(sh * target);
  else sh = Math.round(sw / target);
  const sx = Math.round((img.naturalWidth - sw) / 2);
  const sy = Math.round((img.naturalHeight - sh) / 2);
  // Never enlarge a small picture
  const scale = Math.min(1, slot.width / sw);
  const width = Math.round(sw * scale);
  const height = Math.round(sh * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // transparent areas become white in a JPG
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.86));
  if (!blob) return { file, adjusted: false };
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return { file: new File([blob], name, { type: "image/jpeg" }), adjusted: true, width, height };
}

// Photos over this size are compressed before uploading: to half their size or less (and under maxBytes)
const BIG_PHOTO = 2 * 1024 * 1024;
// Largest photo that can be chosen at all; the server compresses it again and applies the kind's limit
export const MAX_PHOTO_BYTES = 20 * 1024 * 1024;

// Re-encodes a picture smaller and smaller (quality first, then size) until it is at most `target` bytes. WebP keeps a
// logo's transparency where the browser can write WebP; otherwise JPEG on white. GIFs are left to the server.
async function shrink(file, target) {
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const webp = canvas.toDataURL("image/webp").startsWith("data:image/webp");
  const type = webp ? "image/webp" : "image/jpeg";
  let scale = Math.min(1, 2000 / Math.max(img.naturalWidth, img.naturalHeight));
  let best = null;
  for (let round = 0; round < 6; round += 1) {
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!webp) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const quality of [0.82, 0.7, 0.55]) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, quality));
      if (blob && (!best || blob.size < best.size)) best = blob;
      if (blob && blob.size <= target) return new File([blob], file.name.replace(/\.[^.]+$/, "") + (webp ? ".webp" : ".jpg"), { type });
    }
    scale *= 0.8;
  }
  return best ? new File([best], file.name.replace(/\.[^.]+$/, "") + (webp ? ".webp" : ".jpg"), { type }) : file;
}

// fitImage(), then, for a photo that is still over 2 MB, compression to half the original size or less and under
// maxBytes. Returns { file, compressed } where compressed says it was made smaller this way.
export async function prepareUpload(file, kind, maxBytes) {
  const fitted = await fitImage(file, kind);
  if (fitted.file.size <= BIG_PHOTO || file.type === "image/gif") return { file: fitted.file, compressed: fitted.file.size < file.size };
  const target = Math.min(maxBytes, Math.floor(file.size / 2));
  return { file: await shrink(fitted.file, target), compressed: true };
}
